import type { BlobLike, ImageArray } from "@pdfking/compatibility"
import type { Pixel } from "@pdfking/math"
import { BlobNative } from "@pdfking/compatibility"

const importWithImageBitmap = async (blob: Blob): Promise<ImageArray> => {
  const bitmap = await createImageBitmap(blob)
  const { height, width } = bitmap

  const canvas = new OffscreenCanvas(width, height)
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("Failed to get canvas 2d context")

  ctx.drawImage(bitmap, 0, 0)
  bitmap.close()

  const imageData = ctx.getImageData(0, 0, width, height)
  return {
    data: new Uint8Array(imageData.data.buffer),
    height: height as Pixel,
    width: width as Pixel,
  }
}

const importWithImage = (blob: Blob): Promise<ImageArray> =>
  Promise.resolve(URL.createObjectURL(blob)).then(
    url =>
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(url)
          const canvas = document.createElement("canvas")
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext("2d")!
          ctx.drawImage(img, 0, 0)
          const imageData = ctx.getImageData(0, 0, img.width, img.height)
          resolve({
            data: new Uint8Array(imageData.data.buffer),
            height: img.height as Pixel,
            width: img.width as Pixel,
          })
        }
        img.onerror = e => {
          URL.revokeObjectURL(url)
          reject(e)
        }
        img.src = url
      })
  )

export const ImportImage = async (blob: BlobLike): Promise<ImageArray> => {
  const nativeBlob = await BlobNative(blob)

  if (typeof OffscreenCanvas !== "undefined") {
    return importWithImageBitmap(nativeBlob)
  }

  if (typeof Image !== "undefined") {
    return importWithImage(nativeBlob)
  }

  throw new Error("No image import implementation available")
}
